import moment from "moment";

export default class VMJState {
    //#region Local Storage Keys
    static _lastCheckedStorageKeyPart = '__vmj_state_last_loaded_';
    static _vmjStateStorageKeyPart = '__vmj_state_storage_';
    static _storedKey = null;
    static _localMapKey = '__vmj_state_storage_map__';
    //#endregion

    //#region External Data
    static _companyId = null;
    static _userId = null;
    //#endregion

    //#region Local Storage Data
    static _lastLoaded = null;
    static _data = null;
    //#endregion

    static async Get(key) {
        await VMJState._Init();
        return VMJState._data[key] ?? null;
    }


    static CompanyUrl(path, file, args)
    {
        if(VMJState._userId == null) { return path.replace(/\/$/, '') + "/" + file.replace(/^\//, ''); }
        let base = path.replace(/\/$/, '') + "/" + VMJState._companyId + "/" + file.replace(/^\//, '');
        if(typeof(args) != undefined && args != null && Object.keys(args).length > 0)
        {
            var params = Object.keys(args).map(function(key) { 
                return key + '=' + args[key]; 
            }).join('&'); 
            base += "?" + params;
        }
        return base;
    }

    static async OrganisationUrl(path, file, args)
    {
        const orgId = (await VMJState.Get('organisation'))?.public_facing_id;
        if(VMJState._userId == null || orgId == null) { return path.replace(/\/$/, '') + "/" + file.replace(/^\//, ''); }
        let base = path.replace(/\/$/, '') + 
            "/" + orgId + 
            "/" + VMJState._companyId + 
            "/" + file.replace(/^\//, '');
        if(typeof(args) != undefined && args != null && Object.keys(args).length > 0)
        {
            var params = Object.keys(args).map(function(key) { 
                return key + '=' + args[key]; 
            }).join('&'); 
            base += "?" + params;
        }
        return base;
    }

    static async AssertAction(modelKey, actionKey) {
        const _userRoles = (await VMJState.Get('user'))?.user_roles;
        if(_userRoles == null) {
            return false;
        }
        if(_userRoles.hasOwnProperty('roles')) {
            for(var i = 0;i < _userRoles.roles.length;i++) {
                const _role = _userRoles.roles[i];
                if(_role.hasOwnProperty('models')) {
                    for(var j = 0;j < _role.models.length;j++) {
                        const _model = _role.models[j];
                        if(_model.model_key == modelKey && _model.hasOwnProperty('action_collection')) {
                            for(var k = 0;k < _model.action_collection.length;k++) {
                                const _action = _model.action_collection[k];
                                if(_action['actionKey'] == actionKey) {
                                    return true;
                                }
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    static async AssertManyActions(permissions) {
        for(var i = 0;i < permissions.length;i++) {
            permissions[i]['assertion'] = false;
            if(permissions[i].hasOwnProperty('modelKey') && permissions[i].hasOwnProperty('actionKey')) {
                permissions[i]['assertion'] = await VMJState.AssertAction(permissions[i]['modelKey'], permissions[i]['actionKey']);
            }
        }
        return permissions;
    }


    static async HasBetaFeature(feature) {
        const features = (await VMJState.Get('user'))?.beta_features;
        if(features == null) {
            return false;
        }
        return features.includes(feature);
    }

    //#region Data Handling

    static async _Init() {
        try  {
            VMJState._Key();
        } catch(error) {
            // User likely isn't logged in
            return;
        }
        if(VMJState._data == null) {
            let _data = null;
            try  {
                _data = localStorage.getItem(VMJState._DataStorageKey());
            } catch(error) {
                console.log(error);
            }
            let shouldReload = _data == null;
            if(_data != null) {
                VMJState._data = JSON.parse(_data);
                
                // Check build code first, and if required trigger an update
                let buildCode = VMJState._data['build_code'] ?? null;
                if(buildCode == null || buildCode != _buildCode) {
                    shouldReload = true;
                }
            }
            
            if(shouldReload == true || VMJState._ShouldReloadState()) {
                await VMJState._LoadData();
            }
        }
    }

    static _GetContext() {
        if(VMJState._companyId == null) {
            VMJState._companyId = _setUserCompanyId ?? null;
        }
        if(VMJState._userId == null) {
            VMJState._userId = _setPublicFacingUserId ?? null;
        }
    }

    static _HasContext() {
        VMJState._GetContext();
        return VMJState._companyId != null && VMJState._companyId.length > 0 && VMJState._userId != null && VMJState._userId.length > 0;
    }

    static _Key() {
        if(VMJState._storedKey == null) {
            if(VMJState._HasContext()) {
                VMJState._storedKey = VMJState._companyId + '_' + VMJState._userId;
            }
        }
        if(VMJState._storedKey == null) {
            throw new Error('Unable to generate storage key for this user');
        }
        return VMJState._storedKey;
    }

    static _LastCheckedStorageKey() {
        return VMJState._LastCheckedStorageKeyManual(VMJState._Key());
    }

    static _DataStorageKey() {
        return VMJState._DataStorageKeyManual(VMJState._Key());
    }

    static _LastCheckedStorageKeyManual(key) {
        return VMJState._lastCheckedStorageKeyPart + key;
    }

    static _DataStorageKeyManual(key) {
        return VMJState._vmjStateStorageKeyPart + key;
    }

    static _ShouldReloadState() {
        try {
            VMJState._lastLoaded ??= localStorage.getItem(VMJState._LastCheckedStorageKey());
        } catch(error) {
            console.log(error);
        }
        let compare = parseInt(moment().format('X')) - (60 * 60);
        return VMJState._lastLoaded < compare;
    }

    static async _LoadData() {
        let _map = VMJState._LoadMap();
        try {
            VMJState._data = await ajaxResponse(
                this,
                GenerateCompanyUrl('/functions/vmj-state/', 'get'),
                null,
                undefined,
                undefined,
                "GET",
                "json"
            ) ?? {};
            localStorage.setItem(VMJState._DataStorageKey(), JSON.stringify(VMJState._data));
            VMJState._lastLoaded = parseInt(moment().format('X'));
            localStorage.setItem(VMJState._LastCheckedStorageKey(), VMJState._lastLoaded);
            _map[VMJState._Key()] = true;
            
        } catch(error) {
            console.log(error);
            VMJState._data = {};
            delete _map[VMJState._Key()];
        }
        VMJState._SaveMap(_map);
    }

    static _LoadMap() {
        let _mapString = localStorage.getItem(VMJState._localMapKey);
        let _map = {};
        if(_mapString != null) {
            _map = JSON.parse(_mapString);
        }
        return _map;
    }

    static _SaveMap(_map) {
        localStorage.setItem(VMJState._localMapKey, JSON.stringify(_map));
    }

    static Reset() {
        let _map = VMJState._LoadMap();
        for(var key in _map) {
            localStorage.removeItem(VMJState._LastCheckedStorageKeyManual(key));
            localStorage.removeItem(VMJState._DataStorageKeyManual(key));
        }
        localStorage.removeItem(VMJState._localMapKey);
        VMJState._data = null;
    }

    //#endregion

    
    static async Debug() {
        await VMJState._Init();
        console.log(VMJState._data);
    }
}